import { CSSProperties, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { clsx, makeKey, styx } from 'modules/Utils';
import useScreen from 'Hooks/useScreen';
import useUser from 'Hooks/useUser';
import PageSymphony from 'elements/PageSymphony';
import CardSymphony from 'elements/CardSymphony';
import InputSymphony, { SelectOptionItem } from 'elements/InputSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';
import Loader from 'elements/Loader';
import Banner from 'elements/Banner';
import TagSymphony from 'elements/TagSymphony';
import Icon from 'elements/Icon';
import TabsSymphony, { TabItem } from 'elements/TabsSymphony';
import TabSymphony from 'elements/TabSymphony';
import {
    getAdminAgentCampaigns_api,
    AdminAgentCampaign,
    GetAdminAgentCampaignsParams,
    bulkProcessAgentCampaigns_api,
    bulkUpdateCampaignStatus_api,
    getAdminAllCampaigns_api,
    AdminAllCampaign,
    GetAdminAllCampaignsParams,
    refundYoutubeCampaign_api,
    RefundYoutubeCampaignParams
} from 'api/adminApi';
import { adminRetryCampaign_api } from 'api/campaignApi';
import { toastError, toastSuccess } from 'modules/Toast';
import Colors from 'modules/Colors';
import Sty from 'modules/Sty';
import { CAMPAIGN_STATUS_INFO, PLAYBOOK_TYPE_INFO, PlaybookType, CampaignStatus, AdPlatform } from 'modules/Const';
import AgentConversationModal from 'components/shareable/AgentConversationModal';
import ModalSymphony from 'elements/ModalSymphony';
import dayjs from 'dayjs';

// Status tab configuration
interface StatusTabConfig {
    id: string;
    title: string;
    statuses: CampaignStatus[];
    color: string;
}

const STATUS_TABS: Record<string, StatusTabConfig> = {
    AWAITING_AGENT_PROCESSING: {
        id: 'awaiting_processing',
        title: 'Awaiting Agent Processing',
        statuses: [CampaignStatus.AWAITING_AGENT_PROCESSING],
        color: 'yellow'
    },
    PROCESSING: {
        id: 'processing',
        title: 'Processing',
        statuses: [CampaignStatus.PROCESSING],
        color: 'yellow'
    },
    TO_RETRY: {
        id: 'to_retry',
        title: 'To Retry',
        statuses: [CampaignStatus.TO_RETRY],
        color: 'yellow'
    },
    PAUSED: {
        id: 'paused',
        title: 'Paused',
        statuses: [CampaignStatus.PAUSED],
        color: 'yellow'
    },
    WITH_ISSUES: {
        id: 'with_issues',
        title: 'With Issues',
        statuses: [CampaignStatus.WITH_ISSUES],
        color: 'yellow'
    },
    IN_REVIEW: {
        id: 'in_review',
        title: 'In Review',
        statuses: [CampaignStatus.IN_REVIEW],
        color: 'yellow'
    },
    ACTIVE: {
        id: 'active',
        title: 'Active',
        statuses: [CampaignStatus.ACTIVE, CampaignStatus.IN_PROGRESS, CampaignStatus.EXECUTING],
        color: 'green'
    },
    NEEDS_REVIEW: {
        id: 'needs_review',
        title: 'Needs Review',
        statuses: [CampaignStatus.ERROR, CampaignStatus.WITH_ISSUES, CampaignStatus.NEEDS_ACTION, CampaignStatus.FAILED],
        color: 'red'
    },
    CANCELLED: {
        id: 'cancelled',
        title: 'Cancelled',
        statuses: [CampaignStatus.CANCELLED, CampaignStatus.STOPPED, CampaignStatus.ARCHIVED],
        color: 'red'
    }
};

const AgentCampaignsPage = () => {
    const history = useHistory();
    const { mobileView } = useScreen();
    const { isSymphonyAdmin } = useUser();

    // State for campaigns and loading - NOW SUPPORTS ALL CAMPAIGNS
    const [campaigns, setCampaigns] = useState<AdminAllCampaign[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [hasLoaded, setHasLoaded] = useState<boolean>(false);

    // Filter states
    const [campaignIdFilter, setCampaignIdFilter] = useState<string>('');
    const [brandIdFilter, setBrandIdFilter] = useState<string>('');
    const [playbookTypeFilter, setPlaybookTypeFilter] = useState<string>('');
    const [adPlatformFilter, setAdPlatformFilter] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [createdAtAfter, setCreatedAtAfter] = useState<string>('');
    const [createdAtBefore, setCreatedAtBefore] = useState<string>('');

    // New state for view mode toggle
    const [viewMode, setViewMode] = useState<'all' | 'agent_only'>('all');

    // Individual refund state
    const [isRefunding, setIsRefunding] = useState<boolean>(false);

    // Multi-select and bulk operations state
    const [selectedCampaignIds, setSelectedCampaignIds] = useState<number[]>([]);
    const [isBulkProcessing, setIsBulkProcessing] = useState<boolean>(false);
    const [isBulkUpdatingStatus, setIsBulkUpdatingStatus] = useState<boolean>(false);
    const [isBulkRefunding, setIsBulkRefunding] = useState<boolean>(false);

    // Agent notes modal state
    const [showAgentNotesModal, setShowAgentNotesModal] = useState<boolean>(false);
    const [selectedCampaignForNotes, setSelectedCampaignForNotes] = useState<AdminAllCampaign | null>(null);

    // Refund modal state
    const [showRefundModal, setShowRefundModal] = useState<boolean>(false);
    const [refundCampaign, setRefundCampaign] = useState<AdminAllCampaign | null>(null);

    // Error modal state
    const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
    const [errorCampaign, setErrorCampaign] = useState<AdminAllCampaign | null>(null);

    // Retry state
    const [isRetryingCampaign, setIsRetryingCampaign] = useState<boolean>(false);
    const [isBulkRetrying, setIsBulkRetrying] = useState<boolean>(false);

    // Tab state
    const [selectedTab, setSelectedTab] = useState<string>(STATUS_TABS.AWAITING_AGENT_PROCESSING.id);

    // Check admin access
    if (!isSymphonyAdmin) {
        return (
            <PageSymphony className="agentCampaignsPageAccessDenied">
                <Banner
                    className="agentCampaignsAccessDeniedBanner"
                    title="Access Denied"
                    description="You need admin privileges to access this page."
                    color="red"
                />
            </PageSymphony>
        );
    }

    // Fetch campaigns - NOW SUPPORTS BOTH ALL CAMPAIGNS AND AGENT-ONLY
    const fetchCampaigns = async (params: GetAdminAllCampaignsParams = {}) => {
        setIsLoading(true);
        try {
            let loadedCampaigns: AdminAllCampaign[] = [];

            if (viewMode === 'agent_only') {
                // Use the original agent campaigns API
                const response = await getAdminAgentCampaigns_api({
                    limit: 100,
                    offset: 0,
                    ...params
                });

                if (response?.campaigns) {
                    // Transform agent campaigns to match all campaigns format
                    const allCampaignsFormat: AdminAllCampaign[] = response.campaigns.map(campaign => ({
                        ...campaign,
                        is_agent_campaign: true,
                        airtable_status: null
                    }));
                    loadedCampaigns = allCampaignsFormat;
                    setCampaigns(allCampaignsFormat);
                } else {
                    toastError('Failed to fetch agent campaigns');
                    setCampaigns([]);
                }
            } else {
                // Use the new all campaigns API
                const response = await getAdminAllCampaigns_api({
                    limit: 100,
                    offset: 0,
                    ...params
                });

                if (response?.campaigns) {
                    loadedCampaigns = response.campaigns;
                    setCampaigns(response.campaigns);
                } else {
                    toastError('Failed to fetch campaigns');
                    setCampaigns([]);
                }
            }

            // Auto-switch to first tab with campaigns if current tab is empty
            if (loadedCampaigns.length > 0) {
                const currentTabConfig = Object.values(STATUS_TABS).find(tab => tab.id === selectedTab);
                if (currentTabConfig) {
                    const currentTabCount = loadedCampaigns.filter(campaign =>
                        currentTabConfig.statuses.includes(campaign.status as CampaignStatus)
                    ).length;

                    // If current tab has no campaigns, find first tab with campaigns
                    if (currentTabCount === 0) {
                        for (const tabConfig of Object.values(STATUS_TABS)) {
                            const tabCount = loadedCampaigns.filter(campaign =>
                                tabConfig.statuses.includes(campaign.status as CampaignStatus)
                            ).length;

                            if (tabCount > 0) {
                                setSelectedTab(tabConfig.id);
                                break;
                            }
                        }
                    }
                }
            }
        } catch (error) {
            console.error(`Error fetching ${viewMode === 'agent_only' ? 'agent campaigns' : 'campaigns'}:`, error);
            toastError('An error occurred while fetching campaigns');
            setCampaigns([]);
        } finally {
            setIsLoading(false);
            setHasLoaded(true);
        }
    };

    // Initial load
    useEffect(() => {
        fetchCampaigns();
    }, []);

    // Refetch when view mode changes
    useEffect(() => {
        // Clear existing data and refetch with current filters
        handleApplyFilters();
        setSelectedCampaignIds([]); // Clear selections when switching modes
    }, [viewMode]);

    // Get current filter parameters
    const getCurrentFilterParams = (): GetAdminAllCampaignsParams => {
        const params: GetAdminAllCampaignsParams = {};

        if (campaignIdFilter) {
            const id = parseInt(campaignIdFilter);
            if (!isNaN(id)) params.campaignId = id;
        }

        if (brandIdFilter) {
            const id = parseInt(brandIdFilter);
            if (!isNaN(id)) params.brandId = id;
        }

        if (playbookTypeFilter) params.playbook_type = playbookTypeFilter;
        if (adPlatformFilter) params.ad_platform = adPlatformFilter;
        if (statusFilter) params.status = statusFilter;
        if (createdAtAfter) params.createdAtAfter = createdAtAfter;
        if (createdAtBefore) params.createdAtBefore = createdAtBefore;

        return params;
    };

    // Apply filters  
    const handleApplyFilters = () => {
        const params = getCurrentFilterParams();
        fetchCampaigns(params);
    };

    // Clear filters
    const handleClearFilters = () => {
        setCampaignIdFilter('');
        setBrandIdFilter('');
        setPlaybookTypeFilter('');
        setAdPlatformFilter('');
        setStatusFilter('');
        setCreatedAtAfter('');
        setCreatedAtBefore('');
        fetchCampaigns();
    };

    // Multi-select handlers - NOW SUPPORTS ALL CAMPAIGNS
    const handleSelectCampaign = (campaignId: number, selected: boolean) => {
        if (selected) {
            setSelectedCampaignIds(prev => [...prev, campaignId]);
        } else {
            setSelectedCampaignIds(prev => prev.filter(id => id !== campaignId));
        }
    };

    const handleSelectAll = (selected: boolean) => {
        if (selected) {
            const filteredCampaigns = getFilteredCampaigns();
            const allCampaignIds = filteredCampaigns.map(c => c.id);
            setSelectedCampaignIds(allCampaignIds);
        } else {
            setSelectedCampaignIds([]);
        }
    };

    // Copy all user emails for current tab
    const handleCopyAllEmails = () => {
        const filteredCampaigns = getFilteredCampaigns();
        const allEmails = filteredCampaigns
            .flatMap(campaign => campaign.brand.user_emails)
            .filter((email, index, array) => array.indexOf(email) === index) // Remove duplicates
            .join(', ');

        navigator.clipboard.writeText(allEmails);
        const tabTitle = Object.values(STATUS_TABS).find(tab => tab.id === selectedTab)?.title || 'current tab';
        toastSuccess(`All emails for ${tabTitle.toLowerCase()} campaigns copied to clipboard!`);
    };

    // Copy emails for a specific campaign
    const handleCopyCampaignEmails = (campaign: AdminAgentCampaign) => {
        const emails = campaign.brand.user_emails.join(', ');
        navigator.clipboard.writeText(emails);
        toastSuccess(`Emails copied for ${campaign.brand.name}!`);
    };

    // Bulk process campaigns (only agent campaigns)
    const handleBulkProcessCampaigns = async () => {
        if (selectedCampaignIds.length === 0) return;

        // Filter to only agent campaigns for processing
        const selectedCampaigns = campaigns.filter(c => selectedCampaignIds.includes(c.id));
        const agentCampaignIds = selectedCampaigns
            .filter(c => c.is_agent_campaign)
            .map(c => c.id);

        if (agentCampaignIds.length === 0) {
            toastError('No agent campaigns selected for processing');
            return;
        }

        try {
            setIsBulkProcessing(true);
            const response = await bulkProcessAgentCampaigns_api(agentCampaignIds);

            if (response.successCount > 0) {
                toastSuccess(`Successfully started processing ${response.successCount} agent campaigns`);
                // Refresh the campaigns list with current filters
                fetchCampaigns(getCurrentFilterParams());
                setSelectedCampaignIds([]);
            }

            if (response.errorCount > 0) {
                toastError(`${response.errorCount} campaigns failed to process`);
            }
        } catch (error) {
            console.error('Error bulk processing campaigns:', error);
            toastError('Failed to process campaigns');
        } finally {
            setIsBulkProcessing(false);
        }
    };

    // Bulk update campaign status to CANCELLED (cancel)
    const handleBulkCancelCampaigns = async () => {
        if (selectedCampaignIds.length === 0) return;

        try {
            setIsBulkUpdatingStatus(true);
            const response = await bulkUpdateCampaignStatus_api(selectedCampaignIds, CampaignStatus.CANCELLED);

            if (response.successCount > 0) {
                toastSuccess(`Successfully cancelled ${response.successCount} campaigns`);
                // Refresh the campaigns list with current filters
                fetchCampaigns(getCurrentFilterParams());
                setSelectedCampaignIds([]);
            }

            if (response.errorCount > 0) {
                toastError(`${response.errorCount} campaigns failed to cancel`);
            }
        } catch (error) {
            console.error('Error bulk cancelling campaigns:', error);
            toastError('Failed to cancel campaigns');
        } finally {
            setIsBulkUpdatingStatus(false);
        }
    };

    // Bulk copy emails for selected campaigns
    const handleBulkCopyEmails = () => {
        if (selectedCampaignIds.length === 0) return;

        const selectedCampaigns = campaigns.filter(c => selectedCampaignIds.includes(c.id));
        const allEmails = selectedCampaigns
            .flatMap(campaign => campaign.brand.user_emails)
            .filter((email, index, array) => array.indexOf(email) === index) // Remove duplicates
            .join(', ');

        navigator.clipboard.writeText(allEmails);
        toastSuccess(`Copied emails from ${selectedCampaignIds.length} selected campaigns!`);
    };

    // Bulk refund YouTube campaigns
    const handleBulkRefundYoutubeCampaigns = async () => {
        if (selectedCampaignIds.length === 0) return;

        const selectedCampaigns = campaigns.filter(c => selectedCampaignIds.includes(c.id));
        const youtubeCampaigns = selectedCampaigns.filter(c =>
            c.playbook_type === PlaybookType.INCREASE_VIDEO_VIEWS &&
            !c.airtable_status?.refunded
        );

        if (youtubeCampaigns.length === 0) {
            const allYoutubeCampaigns = selectedCampaigns.filter(c => c.playbook_type === PlaybookType.INCREASE_VIDEO_VIEWS);
            if (allYoutubeCampaigns.length === 0) {
                toastError('No YouTube campaigns selected for refund');
            } else {
                toastError('Selected YouTube campaigns have already been refunded');
            }
            return;
        }

        const confirmed = window.confirm(
            `Are you sure you want to refund ${youtubeCampaigns.length} YouTube campaigns? This will process actual Stripe refunds and cannot be undone.`
        );
        if (!confirmed) {
            return;
        }

        try {
            setIsBulkRefunding(true);
            let successCount = 0;
            let errorCount = 0;
            let totalRefunds = 0;

            for (const campaign of youtubeCampaigns) {
                try {
                    const response = await refundYoutubeCampaign_api({
                        campaignId: campaign.id
                    });
                    if (response) {
                        successCount++;
                        totalRefunds += response.successfulRefunds;
                    } else {
                        errorCount++;
                    }
                } catch (error) {
                    console.error(`Error refunding campaign ${campaign.id}:`, error);
                    errorCount++;
                }
            }

            if (successCount > 0) {
                toastSuccess(`Successfully processed ${successCount} campaigns with ${totalRefunds} payment refunds`);
                fetchCampaigns(getCurrentFilterParams());
                setSelectedCampaignIds([]);
            }

            if (errorCount > 0) {
                toastError(`${errorCount} YouTube campaigns failed to refund`);
            }
        } catch (error) {
            console.error('Error bulk refunding YouTube campaigns:', error);
            toastError('Failed to refund campaigns');
        } finally {
            setIsBulkRefunding(false);
        }
    };

    // Individual retry campaign
    const handleRetryCampaign = async (campaign: AdminAllCampaign) => {
        if (!canRetryCampaign(campaign)) {
            toastError('Campaign is not eligible for retry');
            return;
        }

        try {
            setIsRetryingCampaign(true);
            const socialPlatform = getSocialPlatform(campaign);
            const response = await adminRetryCampaign_api({
                campaignId: campaign.id,
                socialPlatform
            });

            if (response) {
                toastSuccess(`Campaign ${campaign.id} successfully moved to in_review!`);
                fetchCampaigns(getCurrentFilterParams());
            } else {
                toastError('Failed to retry campaign. Please try again.');
            }
        } catch (error) {
            console.error(`Error retrying campaign ${campaign.id}:`, error);
            toastError('An error occurred while retrying the campaign. Please try again.');
        } finally {
            setIsRetryingCampaign(false);
        }
    };

    // Bulk retry campaigns
    const handleBulkRetryCampaigns = async () => {
        if (selectedCampaignIds.length === 0) return;

        const selectedCampaigns = campaigns.filter(c => selectedCampaignIds.includes(c.id));
        const retryCampaigns = selectedCampaigns.filter(canRetryCampaign);

        if (retryCampaigns.length === 0) {
            toastError('No eligible campaigns selected for retry. Only Meta/TikTok campaigns with TO_RETRY status can be retried.');
            return;
        }

        const confirmed = window.confirm(
            `Are you sure you want to retry ${retryCampaigns.length} campaigns?`
        );
        if (!confirmed) {
            return;
        }

        try {
            setIsBulkRetrying(true);
            let successCount = 0;
            let errorCount = 0;

            // Process sequentially to avoid overwhelming the API
            for (const campaign of retryCampaigns) {
                try {
                    const socialPlatform = getSocialPlatform(campaign);
                    const response = await adminRetryCampaign_api({
                        campaignId: campaign.id,
                        socialPlatform
                    });

                    if (response) {
                        successCount++;
                    } else {
                        errorCount++;
                    }
                } catch (error) {
                    console.error(`Error retrying campaign ${campaign.id}:`, error);
                    errorCount++;
                }
            }

            if (successCount > 0) {
                toastSuccess(`Successfully retried ${successCount} campaigns`);
                fetchCampaigns(getCurrentFilterParams());
                setSelectedCampaignIds([]);
            }

            if (errorCount > 0) {
                toastError(`${errorCount} campaigns failed to retry`);
            }
        } catch (error) {
            console.error('Error bulk retrying campaigns:', error);
            toastError('Failed to retry campaigns');
        } finally {
            setIsBulkRetrying(false);
        }
    };

    // Show agent notes for a campaign
    const handleShowAgentNotes = (campaign: AdminAllCampaign) => {
        setSelectedCampaignForNotes(campaign);
        setShowAgentNotesModal(true);
    };

    // Open Stripe customer dashboard
    const handleOpenStripe = (stripeCustomerId: string) => {
        const stripeUrl = `https://dashboard.stripe.com/customers/${stripeCustomerId}`;
        window.open(stripeUrl, '_blank');
    };

    // Open Stripe payment intent dashboard
    const handleOpenStripePayment = (paymentIntentId: string) => {
        const stripeUrl = `https://dashboard.stripe.com/payments/${paymentIntentId}`;
        window.open(stripeUrl, '_blank');
    };

    // Open refund modal
    const handleOpenRefundModal = (campaign: AdminAllCampaign) => {
        setRefundCampaign(campaign);
        setShowRefundModal(true);
    };

    // Close refund modal
    const handleCloseRefundModal = () => {
        setShowRefundModal(false);
        setRefundCampaign(null);
    };

    // Open error modal
    const handleOpenErrorModal = (campaign: AdminAllCampaign) => {
        setErrorCampaign(campaign);
        setShowErrorModal(true);
    };

    // Close error modal
    const handleCloseErrorModal = () => {
        setShowErrorModal(false);
        setErrorCampaign(null);
    };

    // Refund YouTube campaign with improved UX
    const handleRefundYoutubeCampaign = async () => {
        if (!refundCampaign) {
            toastError('No campaign selected for refund');
            return;
        }

        setIsRefunding(true);

        try {
            const response = await refundYoutubeCampaign_api({
                campaignId: refundCampaign.id
            });

            if (response) {
                const message = response.successfulRefunds > 0
                    ? `Successfully processed campaign ${refundCampaign.id} with ${response.successfulRefunds} payment refund(s)`
                    : `Campaign ${refundCampaign.id} marked as refunded (no payments found to refund)`;

                toastSuccess(message);
                handleCloseRefundModal();
                // Refresh campaigns to update airtable status with current filters
                fetchCampaigns(getCurrentFilterParams());
            } else {
                toastError('Failed to refund campaign. Please try again.');
            }
        } catch (error) {
            console.error('Error refunding YouTube campaign:', error);
            toastError('An error occurred while refunding the campaign. Please try again.');
        } finally {
            setIsRefunding(false);
        }
    };

    // Get status color
    const getStatusColor = (status: string) => {
        return CAMPAIGN_STATUS_INFO[status as CampaignStatus]?.color || 'grey';
    };

    // Get playbook info
    const getPlaybookInfo = (playbookType: string) => {
        return PLAYBOOK_TYPE_INFO[playbookType as PlaybookType] || {
            title: playbookType,
            icon: 'campaign'
        };
    };

    // Check if campaign is eligible for retry
    const canRetryCampaign = (campaign: AdminAllCampaign) => {
        const playbookInfo = getPlaybookInfo(campaign.playbook_type);
        const isEligiblePlatform = playbookInfo.adPlatform === AdPlatform.META || playbookInfo.adPlatform === AdPlatform.TIKTOK;
        const isEligibleStatus = campaign.status === CampaignStatus.TO_RETRY;
        return isEligiblePlatform && isEligibleStatus;
    };

    // Check if campaign has error to display
    const hasError = (campaign: AdminAllCampaign) => {
        return Boolean(campaign.campaign_metadata?.error);
    };

    // Check if campaign is processing
    const isProcessing = (campaign: AdminAllCampaign) => {
        return Boolean(campaign.campaign_metadata?.processing);
    };

    // Map ad platform to social platform for API
    const getSocialPlatform = (campaign: AdminAllCampaign): 'facebook' | 'tiktok' => {
        const playbookInfo = getPlaybookInfo(campaign.playbook_type);
        if (playbookInfo.adPlatform === AdPlatform.META) return 'facebook';
        if (playbookInfo.adPlatform === AdPlatform.TIKTOK) return 'tiktok';
        return 'facebook'; // fallback
    };

    // Get campaigns for selected tab
    const getFilteredCampaigns = () => {
        const tabConfig = Object.values(STATUS_TABS).find(tab => tab.id === selectedTab);
        if (!tabConfig) return campaigns;

        return campaigns.filter(campaign =>
            tabConfig.statuses.includes(campaign.status as CampaignStatus)
        );
    };

    // Get campaign count for tab
    const getCampaignCountForTab = (tabId: string) => {
        const tabConfig = Object.values(STATUS_TABS).find(tab => tab.id === tabId);
        if (!tabConfig) return 0;

        return campaigns.filter(campaign =>
            tabConfig.statuses.includes(campaign.status as CampaignStatus)
        ).length;
    };

    // Generate tabs with counts
    const getTabsWithCounts = (): TabItem[] => {
        return Object.values(STATUS_TABS).map(tabConfig => ({
            id: tabConfig.id,
            title: `${tabConfig.title} (${getCampaignCountForTab(tabConfig.id)})`,
            color: tabConfig.color
        }));
    };

    // Get selected campaigns info for action bar
    const getSelectedCampaignsInfo = () => {
        const selectedCampaigns = campaigns.filter(c => selectedCampaignIds.includes(c.id));
        const agentCampaigns = selectedCampaigns.filter(c => c.is_agent_campaign);
        const youtubeCampaigns = selectedCampaigns.filter(c => c.playbook_type === PlaybookType.INCREASE_VIDEO_VIEWS);
        const refundableYoutubeCampaigns = youtubeCampaigns.filter(c => !c.airtable_status?.refunded);
        const retryCampaigns = selectedCampaigns.filter(canRetryCampaign);

        return {
            total: selectedCampaigns.length,
            agent: agentCampaigns.length,
            youtube: youtubeCampaigns.length,
            retry: retryCampaigns.length,
            canProcess: selectedTab === STATUS_TABS.AWAITING_AGENT_PROCESSING.id && agentCampaigns.length > 0,
            canCancel: selectedCampaigns.length > 0,
            canCopyEmails: selectedCampaigns.length > 0,
            canRefund: refundableYoutubeCampaigns.length > 0,
            canRetry: retryCampaigns.length > 0
        };
    };

    // Render view mode toggle and filters
    const renderFilters = () => {
        const playbookOptions = Object.values(PlaybookType).map(playbook => ({
            value: playbook,
            title: PLAYBOOK_TYPE_INFO[playbook]?.title || playbook
        }));

        const platformOptions = [
            { value: 'facebook', title: 'Facebook/Instagram' },
            { value: 'tiktok', title: 'TikTok' },
            { value: 'youtube', title: 'YouTube' }
        ];

        // Create status options from CAMPAIGN_STATUS_INFO, including the specific ones mentioned
        const statusOptions = Object.entries(CAMPAIGN_STATUS_INFO)
            .filter(([status]) => {
                // Include the specific statuses mentioned by user and other common ones
                const relevantStatuses = [
                    CampaignStatus.ACTIVE,
                    CampaignStatus.IN_PROGRESS,
                    CampaignStatus.EXECUTING,
                    CampaignStatus.AWAITING_AGENT_PROCESSING,
                    CampaignStatus.TO_RETRY,
                    CampaignStatus.WITH_ISSUES,
                    CampaignStatus.PROCESSING,
                    CampaignStatus.IN_REVIEW,
                    CampaignStatus.PAUSED,
                    CampaignStatus.NEEDS_ACTION,
                    CampaignStatus.ERROR,
                    CampaignStatus.FAILED,
                    CampaignStatus.CANCELLED,
                    CampaignStatus.STOPPED,
                    CampaignStatus.ARCHIVED,
                    CampaignStatus.FINISHED,
                    CampaignStatus.DRAFT
                ];
                return relevantStatuses.includes(status as CampaignStatus);
            })
            .map(([status, info]) => ({
                value: status,
                title: info.title
            }))
            .sort((a, b) => a.title.localeCompare(b.title));

        return (
            <CardSymphony
                className="campaignsFilters"
                title="Filters & Settings"
                style={sty.filtersCard}
            >
                <div className="filtersContainer" style={sty.filtersContainer}>
                    {/* View Mode Toggle */}
                    <div className="filterRow" style={sty.filterRow}>
                        <div className="viewModeToggle" style={sty.viewModeToggle}>
                            <span style={sty.viewModeLabel}>View Mode:</span>
                            <ButtonSymphony
                                className={`viewModeButton ${viewMode === 'all' ? 'active' : ''}`}
                                variant={viewMode === 'all' ? 'primary' : 'outlined'}
                                onClick={() => setViewMode('all')}
                                size="small"
                            >
                                All Campaigns
                            </ButtonSymphony>
                            <ButtonSymphony
                                className={`viewModeButton ${viewMode === 'agent_only' ? 'active' : ''}`}
                                variant={viewMode === 'agent_only' ? 'primary' : 'outlined'}
                                onClick={() => setViewMode('agent_only')}
                                size="small"
                            >
                                Agent Campaigns Only
                            </ButtonSymphony>
                        </div>
                    </div>

                    {/* Campaign and Brand ID Filters */}
                    <div className="filterRow" style={sty.filterRow}>
                        <InputSymphony
                            className="campaignIdFilter"
                            placeholder="Campaign ID"
                            value={campaignIdFilter}
                            onChange={setCampaignIdFilter}
                            style={sty.filterInput}
                        />
                        <InputSymphony
                            className="brandIdFilter"
                            placeholder="Brand ID"
                            value={brandIdFilter}
                            onChange={setBrandIdFilter}
                            style={sty.filterInput}
                        />
                    </div>

                    {/* Playbook Type and Platform Filters */}
                    <div className="filterRow" style={sty.filterRow}>
                        <InputSymphony
                            className="playbookTypeFilter"
                            isSelectDropdown={true}
                            options={[
                                { value: '', title: 'All Playbook Types' },
                                ...playbookOptions
                            ] as SelectOptionItem[]}
                            onOptionSelect={(option: any) => setPlaybookTypeFilter(option.value)}
                            value={playbookTypeFilter}
                            style={sty.filterInput}
                            placeholder="Filter by Playbook Type"
                        />
                        <InputSymphony
                            className="adPlatformFilter"
                            isSelectDropdown={true}
                            options={[
                                { value: '', title: 'All Platforms' },
                                ...platformOptions
                            ] as SelectOptionItem[]}
                            onOptionSelect={(option: any) => setAdPlatformFilter(option.value)}
                            value={adPlatformFilter}
                            style={sty.filterInput}
                        />
                    </div>

                    {/* Status Filter */}
                    <div className="filterRow" style={sty.filterRow}>
                        <InputSymphony
                            className="statusFilter"
                            isSelectDropdown={true}
                            options={[
                                { value: '', title: 'All Statuses' },
                                ...statusOptions
                            ] as SelectOptionItem[]}
                            onOptionSelect={(option: any) => setStatusFilter(option.value)}
                            value={statusFilter}
                            style={sty.filterInput}
                            placeholder="Filter by Status"
                        />
                    </div>

                    {/* Date Filters */}
                    <div className="filterRow" style={sty.filterRow}>
                        <div className="dateFilterContainer" style={sty.dateFilterContainer}>
                            <label style={sty.dateFilterLabel}>Created After:</label>
                            <InputSymphony
                                className="createdAtAfterFilter"
                                type="date"
                                value={createdAtAfter}
                                onChange={setCreatedAtAfter}
                                style={sty.filterInput}
                                placeholder="Select start date"
                            />
                        </div>
                        <div className="dateFilterContainer" style={sty.dateFilterContainer}>
                            <label style={sty.dateFilterLabel}>Created Before:</label>
                            <InputSymphony
                                className="createdAtBeforeFilter"
                                type="date"
                                value={createdAtBefore}
                                onChange={setCreatedAtBefore}
                                style={sty.filterInput}
                                placeholder="Select end date"
                            />
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="filterButtons" style={sty.filterButtons}>
                        <ButtonSymphony
                            className="copyEmailsButton"
                            variant="outlined"
                            onClick={handleCopyAllEmails}
                            disabled={getFilteredCampaigns().length === 0}
                        >
                            <Icon className="copyIcon" icon="copy" size={16} style={{ marginRight: 8 }} />
                            Copy All Emails
                        </ButtonSymphony>
                        <ButtonSymphony
                            className="applyFiltersButton"
                            onClick={handleApplyFilters}
                            isLoading={isLoading}
                            disabled={isLoading}
                        >
                            Apply Filters
                        </ButtonSymphony>
                        <ButtonSymphony
                            className="clearFiltersButton"
                            variant="outlined"
                            onClick={handleClearFilters}
                            disabled={isLoading}
                        >
                            Clear Filters
                        </ButtonSymphony>
                    </div>
                </div>
            </CardSymphony>
        );
    };

    // Render campaign tags
    const renderCampaignTags = (campaign: AdminAllCampaign, playbookInfo: any, statusColor: string, isYoutubeCampaign: boolean, hasAirtableStatus: boolean) => {
        const tags = [
            // Status tag (always present)
            <TagSymphony
                key="status"
                className="statusTag"
                title={CAMPAIGN_STATUS_INFO[campaign.status as CampaignStatus]?.title || campaign.status}
                color={statusColor as any}
                size="small"
            />,
            // Playbook tag (always present)
            <TagSymphony
                key="playbook"
                className="playbookTag"
                title={playbookInfo.title}
                icon={playbookInfo.icon}
                size="small"
            />
        ];

        // Platform tag (conditional)
        if (campaign.platform) {
            tags.push(
                <TagSymphony
                    key="platform"
                    className="platformTag"
                    title={campaign.platform}
                    size="small"
                    color="purple"
                />
            );
        }

        // Express campaign tag (conditional)
        if (campaign.is_express_campaign && PLAYBOOK_TYPE_INFO[campaign.playbook_type as PlaybookType]?.adPlatform === AdPlatform.META) {
            tags.push(
                <TagSymphony
                    key="express"
                    className="expressTag"
                    title="Express Setup"
                    size="small"
                    color="yellow"
                />
            );
        }

        // Manual campaign tag (conditional)
        if (!campaign.is_agent_campaign) {
            tags.push(
                <TagSymphony
                    key="manual"
                    className="manualCampaignTag"
                    title="Built by Campaign Creator"
                    size="small"
                    color="grey"
                />
            );
        }

        // Refunded tag for YouTube campaigns (conditional)
        if (isYoutubeCampaign && campaign.airtable_status?.refunded) {
            tags.push(
                <TagSymphony
                    key="refunded"
                    className="refundedTag"
                    title="Refunded"
                    size="small"
                    color="red"
                />
            );
        }

        // Processing tag (conditional)
        if (isProcessing(campaign)) {
            tags.push(
                <TagSymphony
                    key="processing"
                    className="processingTag"
                    title="Currently being created"
                    size="small"
                    color="orange"
                />
            );
        }

        // Airtable status tag (conditional)
        if (hasAirtableStatus) {
            tags.push(
                <TagSymphony
                    key="airtable"
                    className="airtableStatusTag"
                    title={`Airtable: ${campaign.airtable_status?.status}`}
                    size="small"
                    color={campaign.airtable_status?.status === 'cancelled' ? 'red' : 'blue'}
                />
            );
        }

        return <div className="campaignTags" style={sty.campaignTags}>{tags}</div>;
    };

    // Render action buttons
    const renderActionButtons = (campaign: AdminAllCampaign, canShowAgentNotes: boolean, isYoutubeCampaign: boolean) => {
        const buttons = [];

        // Copy emails button (always present)
        buttons.push(
            <ButtonSymphony
                key="copy-emails"
                className="copyEmailsButton"
                variant="outlined"
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    handleCopyCampaignEmails(campaign);
                }}
                disabled={campaign.brand.user_emails.length === 0}
            >
                <Icon className="copyIcon" icon="copy" size={14} style={{ marginRight: 4 }} />
                Copy Emails
            </ButtonSymphony>
        );

        // View Error button (conditional)
        if (hasError(campaign)) {
            buttons.push(
                <ButtonSymphony
                    key="view-error"
                    className="viewErrorButton"
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleOpenErrorModal(campaign);
                    }}
                >
                    <Icon className="errorIcon" icon="warning" size={14} style={{ marginRight: 4 }} />
                    View Error
                </ButtonSymphony>
            );
        }

        // Agent notes button (conditional)
        if (canShowAgentNotes) {
            buttons.push(
                <ButtonSymphony
                    key="agent-notes"
                    className="agentNotesButton"
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleShowAgentNotes(campaign);
                    }}
                >
                    View Agent Notes
                </ButtonSymphony>
            );
        }

        // Stripe customer button (conditional)
        if (campaign.brand.stripe_customer_id) {
            buttons.push(
                <ButtonSymphony
                    key="stripe-customer"
                    className="stripeButton"
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleOpenStripe(campaign.brand.stripe_customer_id!);
                    }}
                >
                    View Stripe Customer
                </ButtonSymphony>
            );
        }

        // Stripe payment button (conditional)
        if (campaign.payment_intents && campaign.payment_intents.length > 0) {
            buttons.push(
                <ButtonSymphony
                    key="stripe-payment"
                    className="stripePaymentButton"
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleOpenStripePayment(campaign.payment_intents![0].id);
                    }}
                >
                    <Icon className="paymentIcon" icon="dollarSign" size={14} style={{ marginRight: 4 }} />
                    View Stripe Payment
                </ButtonSymphony>
            );
        }

        // Retry Campaign button (conditional)
        if (canRetryCampaign(campaign)) {
            buttons.push(
                <ButtonSymphony
                    key="retry"
                    className="retryButton"
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRetryCampaign(campaign);
                    }}
                    isLoading={isRetryingCampaign}
                    disabled={isRetryingCampaign}
                >
                    <Icon className="retryIcon" icon="reload" size={14} style={{ marginRight: 4 }} />
                    Retry Campaign
                </ButtonSymphony>
            );
        }

        // Refund button (conditional) - only show if not already refunded
        if (isYoutubeCampaign && !campaign.airtable_status?.refunded) {
            buttons.push(
                <ButtonSymphony
                    key="refund"
                    className="refundButton"
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleOpenRefundModal(campaign);
                    }}
                >
                    <Icon className="refundIcon" icon="reload" size={14} style={{ marginRight: 4 }} />
                    Refund Campaign
                </ButtonSymphony>
            );
        }

        // View campaign button (always present, primary action)
        buttons.push(
            <ButtonSymphony
                key="view-campaign"
                className="viewCampaignButton"
                variant="primary"
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    history.push(`/marketing/campaigns/${campaign.id}`);
                }}
            >
                View Campaign
            </ButtonSymphony>
        );

        return <div className="campaignActionButtons" style={sty.campaignActionButtons}>{buttons}</div>;
    };

    // Render campaign metadata
    const renderCampaignMetadata = (campaign: AdminAllCampaign, hasAirtableStatus: boolean) => {
        const metadataItems = [];

        // Created date (always present)
        metadataItems.push(
            <div key="created" className="metadataItem" style={sty.metadataItem}>
                <span style={Sty.text12grey}>Created: </span>
                <span style={Sty.text12}>
                    {dayjs(campaign.createdAt).format('MMM DD, YYYY')}
                </span>
            </div>
        );

        // Marketing plan ID (conditional)
        if (campaign.marketing_plan_id) {
            metadataItems.push(
                <div key="plan-id" className="metadataItem" style={sty.metadataItem}>
                    <span style={Sty.text12grey}>Plan ID: </span>
                    <span style={Sty.text12}>{campaign.marketing_plan_id}</span>
                </div>
            );
        }

        // Chat ID (conditional)
        if (campaign.chat_id) {
            metadataItems.push(
                <div key="chat-id" className="metadataItem" style={sty.metadataItem}>
                    <span style={Sty.text12grey}>Chat ID: </span>
                    <span style={Sty.text12}>{campaign.chat_id}</span>
                </div>
            );
        }

        // Refund URL (conditional)
        if (hasAirtableStatus && campaign.airtable_status?.refund_url) {
            metadataItems.push(
                <div key="refund-url" className="metadataItem" style={sty.metadataItem}>
                    <span style={Sty.text12grey}>Refund URL: </span>
                    <a
                        href={campaign.airtable_status.refund_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ ...Sty.text12, color: Colors.blue }}
                    >
                        View Refund
                    </a>
                </div>
            );
        }

        return <div className="campaignMetadata" style={sty.campaignMetadata}>{metadataItems}</div>;
    };

    // Render campaign row - NOW SUPPORTS ALL CAMPAIGNS WITH CONDITIONAL FEATURES
    const renderCampaignRow = (campaign: AdminAllCampaign, index: number) => {
        const playbookInfo = getPlaybookInfo(campaign.playbook_type);
        const statusColor = getStatusColor(campaign.status);
        const isSelected = selectedCampaignIds.includes(campaign.id);
        const canShowAgentNotes = campaign.status !== CampaignStatus.AWAITING_AGENT_PROCESSING && campaign.agent_notes?.conversation;
        const showCheckbox = true; // Show checkboxes on all tabs for all campaigns
        const isYoutubeCampaign = campaign.playbook_type === PlaybookType.INCREASE_VIDEO_VIEWS;
        const hasAirtableStatus = Boolean(isYoutubeCampaign && campaign.airtable_status);

        return (
            <div
                key={makeKey('agent-campaign', campaign.id, index)}
                className="agentCampaignRow"
                style={{
                    ...sty.campaignRow,
                    backgroundColor: isSelected ? Colors.purpleLightest : Colors.white,
                    border: isSelected ? `2px solid ${Colors.purple}` : sty.campaignRow.border
                }}
            >
                <div className="campaignRowContent" style={sty.campaignRowContent}>
                    {showCheckbox && (
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                                e.stopPropagation();
                                handleSelectCampaign(campaign.id, e.target.checked);
                            }}
                            style={sty.checkbox}
                        />
                    )}
                    <div className="campaignBasicInfo" style={sty.campaignBasicInfo}>
                        <div className="campaignIdAndBrand" style={sty.campaignIdAndBrand}>
                            <div className="campaignId" style={Sty.text16bold}>
                                Campaign #{campaign.id}
                            </div>
                            <div className="brandInfo" style={sty.brandInfo}>
                                <span style={Sty.text14grey}>Brand: </span>
                                <span style={Sty.text14}>
                                    {campaign.brand.name} (ID: {campaign.brand_id})
                                </span>
                            </div>
                            <div className="userEmails" style={sty.userEmails}>
                                <span style={Sty.text12grey}>Emails: </span>
                                <span style={Sty.text12}>
                                    {campaign.brand.user_emails.join(', ') || 'No emails'}
                                </span>
                            </div>
                        </div>
                        {renderCampaignTags(campaign, playbookInfo, statusColor, isYoutubeCampaign, hasAirtableStatus)}
                        {renderActionButtons(campaign, canShowAgentNotes, isYoutubeCampaign)}
                    </div>
                    {renderCampaignMetadata(campaign, hasAirtableStatus)}
                </div>
            </div>
        );
    };

    // Render campaigns list
    const renderCampaignsList = () => {
        const filteredCampaigns = getFilteredCampaigns();

        if (isLoading) {
            return (
                <Loader
                    className="agentCampaignsLoader"
                    title="Loading Agent Campaigns..."
                    isCentered
                />
            );
        }

        if (hasLoaded && filteredCampaigns.length === 0) {
            const tabConfig = Object.values(STATUS_TABS).find(tab => tab.id === selectedTab);
            return (
                <Banner
                    className="noCampaignsBanner"
                    title="No Campaigns Found"
                    description={`No campaigns found with ${tabConfig?.title.toLowerCase()} status.`}
                    color="grey"
                />
            );
        }

        return (
            <CardSymphony
                className="agentCampaignsList"
                title={
                    <div className="campaignsListHeader" style={sty.campaignsListHeader}>
                        <div className="selectAllContainer" style={sty.selectAllContainer}>
                            <input
                                type="checkbox"
                                checked={
                                    filteredCampaigns.length > 0 &&
                                    selectedCampaignIds.length === filteredCampaigns.length
                                }
                                onChange={(e) => handleSelectAll(e.target.checked)}
                                style={sty.checkbox}
                            />
                            <span style={sty.selectAllText}>
                                {Object.values(STATUS_TABS).find(tab => tab.id === selectedTab)?.title} Campaigns ({filteredCampaigns.length})
                                {viewMode === 'all' && (
                                    <span style={sty.agentCampaignCount}>
                                        {' '}({filteredCampaigns.filter(c => c.is_agent_campaign).length} agent)
                                    </span>
                                )}
                            </span>
                        </div>
                    </div>
                }
                style={sty.campaignsListCard}
            >
                <div className="campaignsContainer" style={sty.campaignsContainer}>
                    {filteredCampaigns.map(renderCampaignRow)}
                </div>
            </CardSymphony>
        );
    };

    // Render bottom action bar for bulk operations - NOW SUPPORTS ALL CAMPAIGNS
    const renderBottomActionBar = () => {
        const selectionInfo = getSelectedCampaignsInfo();

        if (selectionInfo.total === 0) return null;

        return (
            <div className="bottomActionBar" style={sty.bottomActionBar}>
                <div className="bottomActionBarContent" style={sty.bottomActionBarContent}>
                    <div className="selectedInfo" style={sty.selectedInfo}>
                        <span style={Sty.text16bold}>
                            {selectionInfo.total} campaigns selected
                        </span>
                        {selectionInfo.agent > 0 && (
                            <span style={{ ...Sty.text14grey, marginLeft: 8 }}>
                                ({selectionInfo.agent} agent)
                            </span>
                        )}
                        {selectionInfo.youtube > 0 && (
                            <span style={{ ...Sty.text14grey, marginLeft: 8 }}>
                                ({selectionInfo.youtube} YouTube)
                            </span>
                        )}
                        {selectionInfo.retry > 0 && (
                            <span style={{ ...Sty.text14grey, marginLeft: 8 }}>
                                ({selectionInfo.retry} retry eligible)
                            </span>
                        )}
                    </div>
                    <div className="actionButtons" style={sty.actionButtons}>
                        <ButtonSymphony
                            className="cancelSelectionButton"
                            variant="outlined"
                            onClick={() => setSelectedCampaignIds([])}
                        >
                            Clear Selection
                        </ButtonSymphony>
                        {selectionInfo.canCopyEmails && (
                            <ButtonSymphony
                                className="bulkCopyEmailsButton"
                                variant="outlined"
                                onClick={handleBulkCopyEmails}
                            >
                                <Icon className="copyIcon" icon="copy" size={16} style={{ marginRight: 4 }} />
                                Copy Emails
                            </ButtonSymphony>
                        )}
                        {selectionInfo.canCancel && (
                            <ButtonSymphony
                                className="bulkCancelButton"
                                variant="outlined"
                                onClick={handleBulkCancelCampaigns}
                                isLoading={isBulkUpdatingStatus}
                                disabled={isBulkUpdatingStatus}
                            >
                                Cancel Campaigns
                            </ButtonSymphony>
                        )}
                        {selectionInfo.canRefund && (
                            <ButtonSymphony
                                className="bulkRefundButton"
                                variant="outlined"
                                onClick={handleBulkRefundYoutubeCampaigns}
                                isLoading={isBulkRefunding}
                                disabled={isBulkRefunding}
                            >
                                <Icon className="refundIcon" icon="reload" size={16} style={{ marginRight: 4 }} />
                                Refund YouTube Campaigns
                            </ButtonSymphony>
                        )}
                        {selectionInfo.canRetry && (
                            <ButtonSymphony
                                className="bulkRetryButton"
                                variant="outlined"
                                onClick={handleBulkRetryCampaigns}
                                isLoading={isBulkRetrying}
                                disabled={isBulkRetrying}
                            >
                                <Icon className="retryIcon" icon="reload" size={16} style={{ marginRight: 4 }} />
                                Retry Campaigns
                            </ButtonSymphony>
                        )}
                        {selectionInfo.canProcess && (
                            <ButtonSymphony
                                className="bulkProcessButton"
                                variant="primary"
                                onClick={handleBulkProcessCampaigns}
                                isLoading={isBulkProcessing}
                                disabled={isBulkProcessing}
                            >
                                Process Agent Campaigns
                            </ButtonSymphony>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Handle tab change and clear selections
    const handleTabChange = (tab: TabItem | string) => {
        const tabId = typeof tab === 'string' ? tab : tab.id;
        setSelectedTab(tabId);
        setSelectedCampaignIds([]); // Clear selections when switching tabs
    };

    // Render tabs component
    const renderTabsComponent = () => {
        return (
            <TabsSymphony
                className="agentCampaignsTabs"
                tabs={getTabsWithCounts()}
                selectedTab={selectedTab}
                onChange={handleTabChange}
                style={sty.tabsContainer}
            />
        );
    };

    // Render tab content
    const renderTabContent = () => {
        return (
            <TabSymphony
                className="agentCampaignsTabContent"
                visible={true}
            >
                {renderCampaignsList()}
            </TabSymphony>
        );
    };

    return (
        <PageSymphony className="agentCampaignsPage">
            <div className="agentCampaignsPageContent" style={sty.pageContent}>
                <div className="agentCampaignsHeader" style={sty.header}>
                    <div className="headerContent" style={sty.headerContent}>
                        <div className="headerText" style={sty.headerText}>
                            <h1 style={{ ...Sty.text32, fontWeight: 'bold' }}>
                                {viewMode === 'all' ? 'All Campaigns' : 'Agent Campaigns'}
                            </h1>
                            <p style={Sty.text16grey}>
                                {viewMode === 'all'
                                    ? 'View and manage all campaigns across all platforms and types'
                                    : 'View and manage AI agent-generated campaigns'
                                }
                            </p>
                        </div>
                        <ButtonSymphony
                            className="refreshButton"
                            onClick={() => fetchCampaigns(getCurrentFilterParams())}
                            isLoading={isLoading}
                            disabled={isLoading}
                            variant="outlined"
                            style={sty.refreshButton}
                        >
                            <Icon className="refreshIcon" icon="reload" size={16} style={{ marginRight: 8 }} />
                            Refresh
                        </ButtonSymphony>
                    </div>
                </div>

                {renderFilters()}
                {renderTabsComponent()}
                {renderTabContent()}
            </div>

            {renderBottomActionBar()}

            <AgentConversationModal
                isOpen={showAgentNotesModal}
                onClose={() => setShowAgentNotesModal(false)}
                campaignId={selectedCampaignForNotes?.id!}
            />

            {/* Refund Modal - Automatic refund processing */}
            <ModalSymphony
                className="refundModal"
                isOpen={showRefundModal}
                onClose={handleCloseRefundModal}
                title="Refund YouTube Campaign"
            >
                <div style={{ padding: '20px' }}>
                    <div style={{ marginBottom: '20px' }}>
                        <p style={{ ...Sty.text16, marginBottom: '8px' }}>
                            Campaign: <strong>#{refundCampaign?.id}</strong>
                        </p>
                        <p style={{ ...Sty.text14grey, marginBottom: '16px' }}>
                            Brand: {refundCampaign?.brand.name}
                        </p>
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        <p style={{ ...Sty.text14, marginBottom: '12px' }}>
                            This will automatically:
                        </p>
                        <ul style={{ ...Sty.text14grey, paddingLeft: '20px', marginBottom: '16px' }}>
                            <li>Process Stripe refunds for all associated payments</li>
                            <li>Mark the campaign as refunded in Airtable</li>
                            <li>Update the campaign status to cancelled</li>
                        </ul>
                        <p style={{ ...Sty.text14, color: Colors.red, fontWeight: 'bold' }}>
                            This action cannot be undone.
                        </p>
                    </div>

                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <ButtonSymphony
                            className="cancelRefundButton"
                            variant="outlined"
                            onClick={handleCloseRefundModal}
                            disabled={isRefunding}
                        >
                            Cancel
                        </ButtonSymphony>
                        <ButtonSymphony
                            className="confirmRefundButton"
                            variant="primary"
                            onClick={handleRefundYoutubeCampaign}
                            isLoading={isRefunding}
                            disabled={isRefunding}
                        >
                            Process Refund
                        </ButtonSymphony>
                    </div>
                </div>
            </ModalSymphony>

            {/* Error Modal - Display campaign error */}
            <ModalSymphony
                className="errorModal"
                isOpen={showErrorModal}
                onClose={handleCloseErrorModal}
                title={`Error for Campaign: ${errorCampaign?.id}`}
                buttonLeftText='Close'
                onClickButtonLeft={handleCloseErrorModal}
                subtitle={errorCampaign ? `Playbook Type: ${getPlaybookInfo(errorCampaign.playbook_type).title} / Ad Platform: ${getPlaybookInfo(errorCampaign.playbook_type).adPlatform}` : ''}
            >
                <div style={{ marginBottom: '20px' }}>
                    <p style={{ ...Sty.text16, marginBottom: '16px', fontWeight: 'bold' }}>
                        Error Details:
                    </p>
                    <div style={{
                        backgroundColor: Colors.greyLight,
                        border: `1px solid ${Colors.grey}`,
                        borderRadius: '8px',
                        padding: '16px',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        maxHeight: '400px',
                        overflowY: 'auto',
                        whiteSpace: 'pre-wrap'
                    }}>
                        {errorCampaign?.campaign_metadata?.error ?
                            JSON.stringify(errorCampaign.campaign_metadata.error, null, 2) :
                            'No error data available'
                        }
                    </div>
                </div>
            </ModalSymphony>
        </PageSymphony>
    );
};

const sty: Record<string, CSSProperties> = {
    pageContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        maxWidth: 1200,
        margin: '0 auto',
        padding: 24
    },
    header: {
        marginBottom: 16
    },
    headerContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 24
    },
    headerText: {
        textAlign: 'center',
        flex: 1
    },
    refreshButton: {
        display: 'flex',
        alignItems: 'center',
        minWidth: 'auto'
    },
    filtersCard: {
        backgroundColor: Colors.white
    },
    filtersContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16
    },
    filterRow: {
        display: 'flex',
        gap: 16,
        flexWrap: 'wrap'
    },
    filterInput: {
        flex: 1,
        minWidth: 200
    },
    filterButtons: {
        display: 'flex',
        gap: 12,
        justifyContent: 'flex-end'
    },
    campaignsListCard: {
        backgroundColor: Colors.white
    },
    viewModeToggle: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flex: 1
    },
    viewModeLabel: {
        fontWeight: 'bold',
        fontSize: 14,
        color: Colors.grey
    },
    agentCampaignCount: {
        fontSize: 12,
        color: Colors.grey,
        fontStyle: 'italic'
    },
    campaignsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1
    },
    campaignRow: {
        display: 'flex',
        flexDirection: 'column',
        padding: 16,
        border: `1px solid ${Colors.greyLight}`,
        borderRadius: 8,
        backgroundColor: Colors.white,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        gap: 12
    },
    campaignBasicInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        flex: 1
    },
    campaignIdAndBrand: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4
    },
    brandInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: 4
    },
    campaignTags: {
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap'
    },
    campaignMetadata: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        marginTop: 8
    },
    metadataItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 4
    },
    viewIcon: {
        color: Colors.grey,
        marginLeft: 16
    },
    campaignsListHeader: {
        display: 'flex',
        alignItems: 'center',
        width: '100%'
    },
    selectAllContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8
    },
    selectAllText: {
        fontWeight: 'bold',
        fontSize: 18
    },
    checkbox: {
        width: 16,
        height: 16,
        cursor: 'pointer'
    },
    campaignRowContent: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 16,
        flex: 1
    },
    userEmails: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        marginTop: 4
    },
    campaignActionButtons: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 12
    },
    bottomActionBar: {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: Colors.white,
        borderTop: `1px solid ${Colors.greyLight}`,
        padding: '16px 24px',
        zIndex: 1000,
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)'
    },
    bottomActionBarContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        maxWidth: 1200,
        margin: '0 auto'
    },
    selectedInfo: {
        display: 'flex',
        alignItems: 'center'
    },
    actionButtons: {
        display: 'flex',
        gap: 12
    },
    tabsContainer: {
        backgroundColor: Colors.white,
        borderRadius: 8,
        padding: '0 16px',
        marginBottom: 24,
        border: `1px solid ${Colors.greyLight}`
    },
    dateFilterContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        flex: 1,
        minWidth: 200
    },
    dateFilterLabel: {
        fontSize: 14,
        fontWeight: 'bold',
        color: Colors.grey
    }
};

export default AgentCampaignsPage; 